import React, { useEffect, useRef, useState } from 'react';
import { IPlanHolder } from '../../api-client/autogenerated';
import { useSelector } from 'react-redux';
import { getProjectState } from '../../features/project/selectors';
import { getPrimeBiddersByProject } from '../../models/api/project';
import {
  Card,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Tooltip,
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import CardContent from '@material-ui/core/CardContent';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import PrimeBidderRow from '../../bidding-components/prime-bidders/PrimeBidderRow';
import { Print } from '@material-ui/icons';
import CircularLoader from '../loader/CircularLoader';
import CsvDownloader from 'react-csv-downloader';
import CSVIcon from '../icons/CSV-icon';
import { useReactToPrint } from 'react-to-print';
import { formatPhoneNumber } from '../../scripts/utils';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      minHeight: '100%',
      width: '100%',
      marginBottom: 32,
    },
    list: {
      padding: '0px 16px 16px 16px',
      overflow: 'auto',
    },
    textPrimary: {
      color: '#464546',
    },
    textSecondary: {
      color: '#464546',
    },
    center: {
      display: 'flex',
      justifyContent: 'center',
    },
    paginationUl: {
      flexWrap: 'nowrap',
    },
    pageSizeDiv: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: '8px',
      width: '100%',
    },
    paginationDiv: {
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
    },
    paginationContainer: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      padding: '10px 5px 5px 5px',
      color: '#2C69D6',
    },
  }),
);

export default function PrimeBiddersList() {
  const classes = useStyles();
  const currentProject = useSelector(getProjectState);
  const [page, setPage] = useState(0);
  const handleChange = (event: any, value: number) => {
    setPage(value - 1);
  };

  const [primeBidders, setPrimeBidders] = useState<IPlanHolder[]>([]);
  const [allPrimeBidders, setAllPrimeBidders] = useState<IPlanHolder[]>([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [pageSize, setPageSize] = useState(5);

  const printRef = useRef(null);

  useEffect(() => {
    if (currentProject)
      getPrimeBiddersByProject(currentProject.id)
        .then(({ results }) => setPrimeBidders(results))
        .catch(() => setPrimeBidders([]));
  }, [currentProject]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onBeforeGetContent: () =>
      new Promise<void>((resolve) => {
        setIsPrinting(true);
        setTimeout(async () => {
          if (allPrimeBidders.length === 0) {
            const { results } = await getPrimeBiddersByProject(currentProject!.id, 1000);
            setAllPrimeBidders(results);
          }
          resolve();
        }, 500);
      }),
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const getCsvData = async () => {
    setCsvLoading(true);

    const { results } = await getPrimeBiddersByProject(currentProject!.id, 1000);
    setAllPrimeBidders(results);

    const rows = results.map((data) => ({
      'License #': data.primeBidder?.contractorLicenseNo
        ? `#${data.primeBidder.contractorLicenseNo}`
        : 'N/A',
      'Company Name': `"${data.primeBidder?.companyName}"` || '',
      'Company Address': `"${[
        data.primeBidder?.address?.address1 || '',
        data.primeBidder?.address?.city ? `${data.primeBidder.address.city},` : '',
        data.primeBidder?.address?.stateOrProvince?.name || '',
        data.primeBidder?.address?.postalCode || '',
      ]
        .filter(Boolean)
        .join(' ')}"`,
      'Contact Name': data.primeBidder?.alternateName || data.primeBidder?.contactName || '',
      'Contact Email': data.primeBidder?.alternateEmail || data.primeBidder?.email || '',
      'Contact Phone':
        formatPhoneNumber(
          data.primeBidder?.alternatePhoneNumber || data.primeBidder?.phoneNumber?.number || '',
        ) || '',
    }));

    setCsvLoading(false);

    return rows;
  };

  return (
    <Card className={classes.root}>
      <CardContent
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <h2 className="h2" style={{ color: '#0947B9', fontSize: '26px', fontWeight: 600 }}>
          Prime Bidders
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          {!isPrinting ? (
            <Tooltip title="Print" arrow placement="top">
              <IconButton disabled={!primeBidders.length} style={{ padding: '3px' }}>
                <Print style={{ display: 'inline-flex' }} onClick={handlePrint} />
              </IconButton>
            </Tooltip>
          ) : (
            <CircularLoader size={24} />
          )}
          <CsvDownloader
            disabled={!primeBidders.length}
            filename={`${currentProject!.name} - Prime Bidders.csv`}
            datas={getCsvData}
            columns={[
              'License #',
              'Company Name',
              'Company Address',
              'Contact Name',
              'Contact Email',
              'Contact Phone',
            ]}
          >
            {csvLoading ? (
              <CircularLoader size={24} />
            ) : (
              <Tooltip title="Export to CSV" arrow placement="left">
                <IconButton disabled={!primeBidders.length} size="small">
                  <CSVIcon style={{ display: 'inline-flex' }} />
                </IconButton>
              </Tooltip>
            )}
          </CsvDownloader>
        </div>
      </CardContent>
      {primeBidders.length === 0 ? (
        <Typography style={{ paddingBottom: 16, paddingLeft: 16 }}>
          There are no prime bidders for this project.
        </Typography>
      ) : (
        <TableContainer>
          <Table ref={printRef}>
            <TableHead style={{ color: 'rgb(97, 96, 97)' }}>
              <TableCell variant="head">License #</TableCell>
              <TableCell variant="head">Company</TableCell>
              <TableCell variant="head">Contact</TableCell>
              <TableCell width={1} />
            </TableHead>
            <TableBody>
              {(isPrinting
                ? allPrimeBidders
                : primeBidders.slice(page * pageSize, page * pageSize + pageSize)
              ).map((row) => (
                <PrimeBidderRow key={row.id} data={row} setPrimeBidders={setPrimeBidders} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <div className={classes.paginationContainer}>
        <div className={classes.paginationDiv}>
          {Math.ceil(primeBidders.length / pageSize) > 1 && (
            <Pagination
              count={Math.ceil(primeBidders.length / pageSize)}
              page={page + 1}
              onChange={handleChange}
              shape="rounded"
              color="primary"
              classes={{ ul: classes.paginationUl }}
            />
          )}
        </div>

        <div className={classes.pageSizeDiv}>
          <p
            style={{
              color: '#616061',
            }}
          >
            Rows per page:
          </p>

          <Select
            id="rowLength-select"
            style={{
              background: 'transparent',
            }}
            value={pageSize}
            onChange={(e) => {
              setPageSize(e.target.value as number);
              setPage(0);
            }}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </div>
      </div>
    </Card>
  );
}
